import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CaretLeft, CheckCircle, Truck } from "@phosphor-icons/react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { Shell } from "@/components/Shell";
import { useApp } from "@/context/AppContext";
import { COUNTRIES, countryRegion } from "@/lib/countries";
import api from "@/lib/api";

const money = (v) => `€ ${Number(v || 0).toFixed(2)}`;

export default function Checkout() {
  const { t, user, cart, clearCart } = useApp();
  const navigate = useNavigate();
  const [shippingOpts, setShippingOpts] = useState([]);
  const [shippingId, setShippingId] = useState(null);
  const [paypal, setPaypal] = useState({ configured: false, client_id: "", currency: "EUR" });
  const [quote, setQuote] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [street, setStreet] = useState("");
  const [houseNumber, setHouseNumber] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("AT");
  const [discountEnabled, setDiscountEnabled] = useState(false);
  const [discountInput, setDiscountInput] = useState("");
  const [discountCode, setDiscountCode] = useState("");
  const [purchased, setPurchased] = useState(null);

  const items = cart.map((i) => ({ product_id: i.product_id, color_id: i.color?.id || null, motif_id: i.motif?.id || null, motif_image_path: i.motif_image_path || null, quantity: i.quantity }));

  useEffect(() => {
    if (cart.length === 0 && !purchased) { navigate("/cart"); return; }
    setEmail(user?.email || "");
    Promise.all([api.get("/shop/shipping"), api.get("/shop/paypal-config"), api.get("/shop/discount/status")])
      .then(([s, cfg, ds]) => { setShippingOpts(s.data); setPaypal(cfg.data); setDiscountEnabled(!!ds.data.enabled); if (s.data.length) setShippingId(s.data[0].id); })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (cart.length === 0) return;
    api.post("/shop/checkout/quote", { items, shipping_option_id: shippingId, country, discount_code: discountCode })
      .then((r) => setQuote(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Fehler"));
  }, [shippingId, country, discountCode, cart.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyDiscount = () => setDiscountCode(discountInput.trim());

  const formValid = name.trim() && /.+@.+\..+/.test(email) && street.trim() && houseNumber.trim()
    && postalCode.trim() && city.trim() && country && (shippingOpts.length === 0 || shippingId);

  const region = countryRegion(country);
  const shipCostFor = (o) => {
    const base = (o.rates && o.rates[region] != null) ? o.rates[region] : o.price;
    return (o.free_above != null && quote && quote.subtotal >= o.free_above) ? 0 : base;
  };

  if (purchased) {
    return (
      <Shell back="/cart">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-5" data-testid="checkout-success">
          <div className="sb-card p-6 text-center">
            <CheckCircle size={44} weight="fill" className="mx-auto text-green-600" />
            <h1 className="text-2xl font-heading font-semibold text-gray-900 mt-2">{t("purchaseSuccessTitle")}</h1>
            <p className="font-body text-gray-600 mt-1">{t("purchaseSuccessDesc")}</p>
            <div className="space-y-1 mt-3">
              {(purchased.codes || []).map((c) => (<p key={c} className="font-heading font-semibold tracking-wide border-2 border-gray-800 rounded-xl py-2 bg-white">{c}</p>))}
            </div>
            <button data-testid="success-back-shop" onClick={() => navigate("/shop")} className="sb-btn w-full py-3 mt-4 text-white font-medium" style={{ background: "var(--app-primary)" }}>{t("continueShopping")}</button>
          </div>
        </motion.div>
      </Shell>
    );
  }

  return (
    <Shell back="/cart">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-5" data-testid="checkout-page">
        <button data-testid="checkout-back" onClick={() => navigate("/cart")} className="flex items-center gap-1 font-heading text-gray-700"><CaretLeft size={18} weight="bold" />{t("cartTitle")}</button>
        <h1 className="text-2xl font-heading font-semibold text-gray-900">{t("checkoutSummary")}</h1>

        {/* Customer form */}
        <div className="sb-card p-4 space-y-3">
          <div>
            <label className="text-xs font-body text-gray-500 uppercase tracking-wider">{t("customerNameLabel")} *</label>
            <input data-testid="checkout-name" className="sb-input mt-1" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <label className="text-xs font-body text-gray-500 uppercase tracking-wider">{t("customerEmailLabel")} *</label>
            <input data-testid="checkout-email" type="email" className="sb-input mt-1" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div>
            <label className="text-xs font-body text-gray-500 uppercase tracking-wider">{t("deliveryAddressLabel")} *</label>
            <div className="grid grid-cols-3 gap-2 mt-1">
              <input data-testid="checkout-street" placeholder={t("streetLabel")} className="sb-input col-span-2" value={street} onChange={(e) => setStreet(e.target.value)} />
              <input data-testid="checkout-house-number" placeholder={t("houseNumberLabel")} className="sb-input" value={houseNumber} onChange={(e) => setHouseNumber(e.target.value)} />
              <input data-testid="checkout-postal-code" placeholder={t("postalCodeLabel")} className="sb-input" value={postalCode} onChange={(e) => setPostalCode(e.target.value)} />
              <input data-testid="checkout-city" placeholder={t("cityLabel")} className="sb-input col-span-2" value={city} onChange={(e) => setCity(e.target.value)} />
              <select data-testid="checkout-country" className="sb-input col-span-3" value={country} onChange={(e) => setCountry(e.target.value)}>
                {COUNTRIES.map((c) => (<option key={c.code} value={c.code}>{c.name}</option>))}
              </select>
            </div>
          </div>
        </div>

        {/* Shipping options */}
        <div className="sb-card p-4">
          <h2 className="font-heading font-semibold text-gray-900 mb-3 flex items-center gap-2"><Truck size={18} weight="bold" />{t("chooseShipping")}</h2>
          {shippingOpts.length === 0 && <p className="font-body text-sm text-gray-500">{t("noShipping")}</p>}
          <div className="space-y-2" data-testid="checkout-shipping-options">
            {shippingOpts.map((o) => (
              <button key={o.id} data-testid={`shipping-${o.id}`} onClick={() => setShippingId(o.id)}
                className={`w-full text-left border-2 rounded-2xl px-3 py-2.5 bg-white flex items-center justify-between ${shippingId === o.id ? "border-gray-900" : "border-gray-300"}`} style={shippingId === o.id ? { boxShadow: "2px 2px 0 0 #1F2937" } : {}}>
                <div className="min-w-0">
                  <p className="font-heading font-semibold text-sm text-gray-900">{o.name}</p>
                  {o.delivery_time && <p className="font-body text-[11px] text-gray-500">{t("deliveryTimeLabel")}: {o.delivery_time}</p>}
                  {o.free_above != null && <p className="font-body text-[11px] text-gray-500">{t("freeLabel")} {t("priceFrom")} {money(o.free_above)}</p>}
                </div>
                <span className="font-heading font-semibold shrink-0">{shipCostFor(o) === 0 ? t("freeLabel") : money(shipCostFor(o))}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Summary */}
        {quote && discountEnabled && (
          <div className="sb-card p-4" data-testid="discount-field">
            <label className="text-xs font-body text-gray-500 uppercase tracking-wider">{t("discountCodeLabel")}</label>
            <div className="flex gap-2 mt-1">
              <input data-testid="discount-input" className="sb-input flex-1 uppercase" value={discountInput} onChange={(e) => setDiscountInput(e.target.value)} placeholder="z. B. SOMMER10" />
              <button data-testid="discount-apply" onClick={applyDiscount} className="sb-btn px-4 bg-white font-medium text-sm">{t("applyLabel")}</button>
            </div>
            {quote.discount_error && <p data-testid="discount-error" className="font-body text-sm text-red-500 mt-2">{quote.discount_error}</p>}
            {quote.discount && <p data-testid="discount-ok" className="font-body text-sm text-green-600 mt-2">{t("discountApplied")}: {quote.discount.code} (−{money(quote.discount.amount)})</p>}
          </div>
        )}

        {quote && (
          <div className="sb-card p-4 space-y-2" data-testid="checkout-summary">
            {quote.items.map((li, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <span className="font-body text-gray-700 truncate pr-2">{li.quantity}× {li.product_name}{li.color_name ? ` · ${li.color_name}` : ""}{li.motif_name ? ` · ${li.motif_name}` : ""}</span>
                <span className="font-heading">{money(li.line_total)}</span>
              </div>
            ))}
            <div className="flex items-center justify-between border-t-2 border-gray-200 pt-2 text-sm">
              <span className="font-body text-gray-600">{t("subtotalLabel")}</span><span data-testid="summary-subtotal" className="font-heading">{money(quote.subtotal)}</span>
            </div>
            {quote.discount && (
              <div className="flex items-center justify-between text-sm text-green-600">
                <span className="font-body">{t("discountLabel")} ({quote.discount.code})</span><span data-testid="summary-discount" className="font-heading">−{money(quote.discount.amount)}</span>
              </div>
            )}
            <div className="flex items-center justify-between text-sm">
              <span className="font-body text-gray-600">{t("shippingCostLabel")}</span><span data-testid="summary-shipping" className="font-heading">{quote.shipping.cost === 0 ? t("freeLabel") : money(quote.shipping.cost)}</span>
            </div>
            <div className="flex items-center justify-between border-t-2 border-gray-800 pt-2">
              <span className="font-heading font-semibold">{t("totalLabel")}</span><span data-testid="summary-total" className="text-xl font-heading font-semibold">{money(quote.total)}</span>
            </div>
          </div>
        )}

        {/* Payment */}
        {!formValid && <p data-testid="fillRequired" className="font-body text-sm text-red-500">{t("fillRequired")}</p>}
        {!paypal.configured && (
          <div className="rounded-2xl border-2 border-amber-500 px-3 py-3 font-body text-sm text-gray-800" style={{ background: "#FFFBEB" }} data-testid="payment-coming-soon">{t("paymentComingSoon")}</div>
        )}
        {paypal.configured && (
          <div data-testid="paypal-area">
            {formValid && (
              <PayPalScriptProvider options={{ clientId: paypal.client_id, currency: paypal.currency, intent: "capture" }}>
                <PayPalButtons
                  style={{ layout: "vertical", shape: "pill" }}
                  createOrder={async () => {
                    const r = await api.post("/shop/checkout/create", { items, shipping_option_id: shippingId, customer_name: name, customer_email: email, street, house_number: houseNumber, postal_code: postalCode, city, country, discount_code: discountCode });
                    return r.data.paypal_order_id;
                  }}
                  onApprove={async (data) => {
                    try {
                      const r = await api.post("/shop/checkout/capture", { order_id: data.orderID });
                      setPurchased({ codes: r.data.codes || [] });
                      clearCart();
                    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
                  }}
                  onError={() => toast.error("PayPal Fehler")}
                />
              </PayPalScriptProvider>
            )}
          </div>
        )}
      </motion.div>
    </Shell>
  );
}
